import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Loader2,
  Rocket,
  ExternalLink,
  Copy,
  Pencil,
  Check,
  Globe,
  Code2,
  Search,
} from 'lucide-react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { supabaseGet, supabasePost } from '@/utils/supabaseRest';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { EmbedCodeGenerator } from '@/components/campaigns/EmbedCodeGenerator';

type CampaignMode = 'embedded' | 'standalone';
type CampaignStatus = 'draft' | 'published' | 'paused' | 'archived';

interface Campaign {
  id: string;
  user_id: string;
  chatbot_id: string;
  slug: string;
  name: string;
  mode: CampaignMode;
  flow_id: string | null;
  destinations: any;
  theme: any;
  status: CampaignStatus;
  created_at?: string;
}

interface ChatbotRow {
  id: string;
  name: string;
}

const STATUS_STYLES: Record<CampaignStatus, string> = {
  draft: 'bg-slate-700 text-slate-200 border-slate-600',
  published: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  paused: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  archived: 'bg-slate-800 text-slate-400 border-slate-700',
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function PortalCampaigns() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const userId = user?.id || null;

  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState<'all' | CampaignMode>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedChatbotId, setEmbedChatbotId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formChatbotId, setFormChatbotId] = useState<string>('');
  const [formMode, setFormMode] = useState<CampaignMode>('standalone');
  const [formSlug, setFormSlug] = useState('');
  const [formSlugEdited, setFormSlugEdited] = useState(false);

  const resetForm = () => {
    setFormName('');
    setFormChatbotId('');
    setFormMode('standalone');
    setFormSlug('');
    setFormSlugEdited(false);
  };

  // Fetch user's chatbots for the selector
  const { data: chatbots } = useQuery({
    queryKey: ['chatbots-for-campaigns', userId],
    queryFn: async () => {
      if (!userId) return [];
      return supabaseGet<ChatbotRow[]>(
        `chatbots?select=id,name&user_id=eq.${userId}&order=created_at.desc`
      );
    },
    enabled: !!userId,
  });

  // Fetch campaigns
  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['campaigns', userId],
    queryFn: async () => {
      if (!userId) return [];
      return supabaseGet<Campaign[]>(
        `campaigns?select=*&user_id=eq.${userId}&order=created_at.desc`
      );
    },
    enabled: !!userId,
  });

  const chatbotNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (chatbots || []).forEach((c) => {
      map[c.id] = c.name;
    });
    return map;
  }, [chatbots]);

  const filteredCampaigns = useMemo(() => {
    const list = campaigns || [];
    return list.filter((c) => {
      if (modeFilter !== 'all' && c.mode !== modeFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (
          !c.name.toLowerCase().includes(s) &&
          !c.slug.toLowerCase().includes(s)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [campaigns, modeFilter, search]);

  // Create campaign mutation
  const createCampaign = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('Not authenticated');
      if (!formName.trim()) throw new Error('Name is required');
      if (!formChatbotId) throw new Error('Please select a chatbot');

      const slug =
        formMode === 'standalone'
          ? slugify(formSlug || formName)
          : slugify(`${formName}-${Date.now()}`);

      if (formMode === 'standalone' && !slug) {
        throw new Error('Please provide a valid slug');
      }

      const insertPayload = {
        user_id: userId,
        chatbot_id: formChatbotId,
        name: formName.trim(),
        slug,
        mode: formMode,
        status: 'draft' as CampaignStatus,
        destinations: [],
        theme: {},
      };

      const data = await supabasePost<Campaign[]>('campaigns', insertPayload);
      if (!data?.[0]) throw new Error('Failed to create campaign');
      return data[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId] });
      toast({ title: 'Campaign created', description: 'Your campaign is ready to configure.' });
      setCreateOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast({
        title: 'Failed to create campaign',
        description: err?.message || 'Something went wrong',
        variant: 'destructive',
      });
    },
  });

  const handleCopyLink = async (campaign: Campaign) => {
    const url =
      campaign.mode === 'standalone'
        ? `${window.location.origin}/c/${campaign.slug}`
        : `${window.location.origin}/chat/${campaign.chatbot_id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(campaign.id);
      setTimeout(() => setCopiedId(null), 2000);
      toast({ title: 'Link copied', description: url });
    } catch (err) {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  const handlePreview = (campaign: Campaign) => {
    const url =
      campaign.mode === 'standalone'
        ? `/c/${campaign.slug}`
        : `/chat/${campaign.chatbot_id}`;
    window.open(url, '_blank');
  };

  const handleEdit = (campaign: Campaign) => {
    if (campaign.mode === 'embedded') {
      setEmbedChatbotId(campaign.chatbot_id);
      setEmbedOpen(true);
    } else {
      navigate(`/portal/builder/${campaign.chatbot_id}`);
    }
  };

  return (
    <PortalLayout>
      <div className="min-h-full bg-slate-950 text-slate-100 -m-0">
        <div className="p-4 md:p-8 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
                <Rocket className="w-6 h-6 text-emerald-400" />
                Campaigns
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                Launch embedded widgets and standalone landing pages for your chatbots.
              </p>
            </div>
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search campaigns..."
                className="pl-9 bg-slate-900 border-slate-800 text-slate-100 placeholder:text-slate-500"
              />
            </div>
            <Select value={modeFilter} onValueChange={(v: any) => setModeFilter(v)}>
              <SelectTrigger className="w-full sm:w-48 bg-slate-900 border-slate-800 text-slate-100">
                <SelectValue placeholder="Mode" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                <SelectItem value="all">All modes</SelectItem>
                <SelectItem value="embedded">Embedded</SelectItem>
                <SelectItem value="standalone">Standalone</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Campaign list */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-10 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
                <Rocket className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">No campaigns yet</h3>
              <p className="text-sm text-slate-400 mb-4">
                Create your first campaign to share your chatbot with the world.
              </p>
              <Button
                onClick={() => setCreateOpen(true)}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCampaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="rounded-xl border border-slate-800 bg-slate-900/60 hover:border-emerald-500/40 transition-colors p-5 flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <h3 className="text-white font-semibold truncate">{campaign.name}</h3>
                      <p className="text-xs text-slate-500 truncate">
                        {campaign.mode === 'standalone' ? `/c/${campaign.slug}` : campaign.slug}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide border ${STATUS_STYLES[campaign.status]}`}
                    >
                      {campaign.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                    {campaign.mode === 'embedded' ? (
                      <Code2 className="w-3.5 h-3.5 text-emerald-400" />
                    ) : (
                      <Globe className="w-3.5 h-3.5 text-emerald-400" />
                    )}
                    <span className="capitalize">{campaign.mode}</span>
                    <span className="text-slate-700">•</span>
                    <span className="truncate">
                      {chatbotNameById[campaign.chatbot_id] || 'Chatbot'}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center mb-4">
                    <div className="rounded-md bg-slate-950/60 border border-slate-800 py-2">
                      <div className="text-sm font-semibold text-white">0</div>
                      <div className="text-[10px] uppercase text-slate-500">Views</div>
                    </div>
                    <div className="rounded-md bg-slate-950/60 border border-slate-800 py-2">
                      <div className="text-sm font-semibold text-white">0</div>
                      <div className="text-[10px] uppercase text-slate-500">Starts</div>
                    </div>
                    <div className="rounded-md bg-slate-950/60 border border-slate-800 py-2">
                      <div className="text-sm font-semibold text-white">0</div>
                      <div className="text-[10px] uppercase text-slate-500">Leads</div>
                    </div>
                  </div>

                  <div className="mt-auto grid grid-cols-3 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(campaign)}
                      className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePreview(campaign)}
                      className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Preview
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyLink(campaign)}
                      className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"
                    >
                      {copiedId === campaign.id ? (
                        <>
                          <Check className="w-3.5 h-3.5 mr-1" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create campaign modal */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="bg-slate-950 border border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-white">Create Campaign</DialogTitle>
            <DialogDescription className="text-slate-400">
              Pick a chatbot and choose how you want to distribute it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                Campaign name
              </label>
              <Input
                value={formName}
                onChange={(e) => {
                  setFormName(e.target.value);
                  if (!formSlugEdited) {
                    setFormSlug(slugify(e.target.value));
                  }
                }}
                placeholder="Spring launch"
                className="bg-slate-900 border-slate-800 text-slate-100"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                Chatbot
              </label>
              <Select value={formChatbotId} onValueChange={setFormChatbotId}>
                <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-100">
                  <SelectValue placeholder="Select a chatbot" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-64">
                  {(chatbots || []).map((bot) => (
                    <SelectItem key={bot.id} value={bot.id}>
                      {bot.name}
                    </SelectItem>
                  ))}
                  {(!chatbots || chatbots.length === 0) && (
                    <div className="px-3 py-2 text-xs text-slate-500">
                      No chatbots found. Create one first.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                Mode
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormMode('embedded')}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    formMode === 'embedded'
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-slate-800 bg-slate-900 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Code2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-semibold text-white">Embedded</span>
                  </div>
                  <p className="text-xs text-slate-400">Add to your existing site with a script tag.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormMode('standalone')}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    formMode === 'standalone'
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-slate-800 bg-slate-900 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Globe className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-semibold text-white">Standalone</span>
                  </div>
                  <p className="text-xs text-slate-400">Hosted landing page at a custom URL.</p>
                </button>
              </div>
            </div>

            {formMode === 'standalone' && (
              <div>
                <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                  Slug
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 whitespace-nowrap">/c/</span>
                  <Input
                    value={formSlug}
                    onChange={(e) => {
                      setFormSlugEdited(true);
                      setFormSlug(slugify(e.target.value));
                    }}
                    placeholder="spring-launch"
                    className="bg-slate-900 border-slate-800 text-slate-100"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  This becomes your public URL. Must be unique.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={() => createCampaign.mutate()}
              disabled={createCampaign.isPending || !formName || !formChatbotId}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
            >
              {createCampaign.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed code modal */}
      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent className="bg-slate-950 border border-slate-800 text-slate-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Embed code</DialogTitle>
            <DialogDescription className="text-slate-400">
              Copy and paste this snippet into your website.
            </DialogDescription>
          </DialogHeader>
          {embedChatbotId && <EmbedCodeGenerator chatbotId={embedChatbotId} />}
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}

export default PortalCampaigns;
